/**
 * Location Maps — shared config types, defaults and default Handlebars templates.
 *
 * Mirrors the server contract in public/api/location-maps-helper.php (validators) and the
 * trusscore.com estimator endpoint's location record (retailermaps PARAMETERS.md §14).
 * THE NormalizedLocation KEY SET IS THE CONTRACT — never rename a key.
 */

export type MapDataset = 'retailers' | 'installers';
export type MapCenterStrategy = 'estimateAddress' | 'visitor' | 'fit';
export type MapUnits = 'km' | 'miles';
export type MapLockPreset = 'none' | 'ca' | 'us' | 'us-lower48' | 'mx' | 'north-america' | 'ky';
export type MapListPosition = 'right' | 'left' | 'below' | 'hidden';

/** One location as served by location-maps.php (image URLs already proxied). */
export interface NormalizedLocation {
  id: string;
  sfid: string;
  dataset: MapDataset;
  type: 'retailer' | 'generic' | 'showroom' | 'installer';
  typeId: number | null;
  name: string;
  street: string;
  street2: string | null;
  city: string;
  region: string;
  regionCode: string | null;
  country: string;
  countryName: string;
  zip: string;
  lat: number;
  lng: number;
  phone: string;
  phoneE164: string | null;
  url: string;
  email: string | null;
  placeId: string;
  stocking: boolean | null;
  displays: boolean | null;
  contactFirst: string;
  contactLast: string;
  buyingGroup: string | null;
  performanceScore: number | null;
  radius: number | null;
  logo: string;
  logoSquare: string;
  pin: string;
  hasProfile: boolean;
  profileUrl: string | null;
  distance: number | null;
  flags: Record<'residential' | 'commercial' | 'agriculture' | 'wcb' | 'sw' | 'dd' | 'nl' | 'rc' | 'tw' | 'trim', boolean>;
  showAddress: boolean;
  cardTpl: string | null;
  infowindowTpl: string | null;
}

export interface LocationMapFilter {
  countries?: string[];
  regions?: string[];
  city?: string;
  buyingGroups?: string[];
  ids?: string[];
  types?: number[];
  stocking?: boolean;
  radiusKm?: number | null;
  radiusAround?: 'estimateAddress' | 'visitor';
  useRetailerFilter?: boolean;
  limit?: number;
  installerFlags?: { residential?: boolean; commercial?: boolean; agriculture?: boolean };
  serves?: boolean;
}

/** Theme token names — identical to the website's retailermaps themes so a theme JSON can be pasted. */
export const THEME_TOKEN_SCHEMA = {
  background: 'color', land: 'color', landInstitution: 'color', greenspace: 'color',
  wood: 'color', sand: 'color', water: 'color', waterway: 'color',
  buildings: 'color', buildingsOutline: 'color',
  roadMinor: 'color', roadMinorCasing: 'color', roadMajor: 'color', roadMajorCasing: 'color',
  motorway: 'color', motorwayCasing: 'color', rail: 'color', aeroway: 'color',
  boundary: 'color', boundaryState: 'color',
  labelText: 'color', labelHalo: 'color', roadLabelText: 'color', waterLabelText: 'color',
  maskColor: 'color', maskOpacity: 'number', lockOutline: 'color', lockOutlineWidth: 'number',
  poiVisible: 'bool', shieldsVisible: 'bool', onewayArrowsVisible: 'bool',
  housenumbersVisible: 'bool', buildingsVisible: 'bool', buildings3dVisible: 'bool',
  hillshadeOpacity: 'number',
  popupBg: 'color', popupFg: 'color', listBg: 'color', cardBg: 'color', cardBorder: 'color', accent: 'color', pinHighlight: 'color',
} as const;
export type ThemeTokenName = keyof typeof THEME_TOKEN_SCHEMA;
export type MapThemeTokens = Partial<Record<ThemeTokenName, string | number | boolean>>;

export interface LocationMapSearch {
  enabled: boolean;
  provider: string | null;
  placeholder?: string;
  countries?: string[];
}

/** Heading shown above an INLINE map — icon + title + subtitle, styled like the Placement Guide. */
export interface LocationMapHeading {
  show: boolean;
  icon: { type: 'lucide'; name: string } | { type: 'upload'; url: string } | null;
  title: string;
  subtitle: string;
}

export interface LocationMapDisplay {
  heading?: LocationMapHeading;
  styleUrl: string | null;
  theme: MapThemeTokens;
  heightPx: number;
  /** In the modal the map fills the viewport, but never taller than this. */
  modalMaxHeightPx?: number;
  /** Under 768px wide (phones), neither placement is taller than this. */
  mobileMaxHeightPx?: number;
  lockPreset: MapLockPreset;
  pinSize: number;
  listPosition: MapListPosition;
  units: MapUnits | 'auto';
  search: LocationMapSearch;
  geolocate: boolean;
  initialCenter: MapCenterStrategy;
  defaultZoom?: number | null;
  showDistance: boolean;
}

export interface LocationMapTemplates { card: string; infowindow: string; noresults: string }

/** Admin shape (admin-location-maps.php). */
export interface AdminLocationMap {
  id: number;
  name: string;
  dataset: MapDataset;
  filter: LocationMapFilter;
  display: LocationMapDisplay;
  templates: LocationMapTemplates;
  strings: Record<string, string>;
  isActive: boolean;
  updatedAt: string;
}

/** What the public config endpoint hands the browser (locale overlaid, settings resolved). */
export interface LocationMapClientConfig {
  id: number;
  name: string;
  dataset: MapDataset;
  display: Omit<LocationMapDisplay, 'styleUrl' | 'units' | 'search'> & { styleUrl: string; units: MapUnits; search: LocationMapSearch & { provider: string } };
  templates: LocationMapTemplates;
  strings: Record<string, string>;
  filterHints: { radiusAround: 'estimateAddress' | 'visitor' | null; radiusKm: number | null; needsVisitorPosition: boolean; countries?: string[] };
  tileApiKey: string | null;
  updatedAt: string;
}

export interface LocationMapMeta {
  mode: string;
  units: MapUnits;
  limit: number;
  count: number;
  total: number;
  setSize: number;
  bounds: [number, number, number, number] | null;
  bbox: [number, number, number, number] | null;
  near: [number, number] | null;
  cached: boolean;
  stale: boolean;
  generated: number;
  complete: boolean;
  center?: { lat: number; lng: number; source: 'estimate' | 'visitor' };
}

/** Copy the {{t "key"}} helper resolves: per-map strings_json first, else results:map.tpl.<key>. */
export const MAP_STRING_KEYS = ['showOnMap', 'directions', 'call', 'website', 'profile', 'contact', 'stocking', 'onDisplay', 'noResultsTitle', 'noResultsBody', 'select', 'selected', 'alreadySent'] as const;
export type MapStringKey = typeof MAP_STRING_KEYS[number];

export const DEFAULT_FILTER: LocationMapFilter = { limit: 300 };

export const DEFAULT_DISPLAY: LocationMapDisplay = {
  heading: { show: false, icon: { type: 'lucide', name: 'map-pinned' }, title: '', subtitle: '' },
  styleUrl: null,
  theme: {},
  heightPx: 480,
  modalMaxHeightPx: 640,
  mobileMaxHeightPx: 360,
  lockPreset: 'none',
  pinSize: 32,
  listPosition: 'right',
  units: 'auto',
  search: { enabled: true, provider: null, placeholder: '', countries: [] },
  geolocate: true,
  initialCenter: 'fit',
  defaultZoom: 11,
  showDistance: true,
};

/**
 * Default templates — a port of the website's tc-rm.tpl.card / infowindow / noresults chunks
 * onto the normalised record, with every human-readable string behind {{t}} so the defaults
 * are fully translatable. Rendered once per location (do NOT wrap in {{#each}}).
 */
export const DEFAULT_TEMPLATES: LocationMapTemplates = {
  card: `<div class="lm-card" data-lm-id="{{id}}">
  <div class="lm-card__head">
    <div class="lm-card__title">
      <h5 class="lm-card__name">{{name}}</h5>
      {{#if hasDistance}}<p class="lm-card__meta">{{distanceText}}</p>{{/if}}
    </div>
    {{#if logoSquare}}<img class="lm-card__logo" src="{{logoSquare}}" alt="{{name}}" width="64" height="64" loading="lazy">{{else}}{{#if logo}}<img class="lm-card__logo lm-card__logo--wide" src="{{logo}}" alt="{{name}}" loading="lazy">{{/if}}{{/if}}
  </div>
  <p class="lm-card__line">{{#if street}}{{street}}<br>{{/if}}{{city}}, {{region}}{{#if zip}} {{zip}}{{/if}}</p>
  {{#if contactFirst}}<p class="lm-card__line lm-card__contact">{{t "contact"}}: {{contactFirst}} {{contactLast}}</p>{{/if}}
  <div class="lm-card__actions">
    <button type="button" class="lm-btn lm-btn--link" data-lm-action="show">{{t "showOnMap"}}</button>
    {{#if phone}}<a class="lm-btn lm-btn--link" href="{{tel phoneE164 phone}}">{{t "call"}} {{phone}}</a>{{/if}}
    <a class="lm-btn lm-btn--link" href="{{directionsUrl this}}" target="_blank" rel="noopener">{{t "directions"}}</a>
    {{#if url}}<a class="lm-btn lm-btn--link" href="{{url}}" target="_blank" rel="noopener">{{t "website"}}</a>{{/if}}
    {{#if profileUrl}}<a class="lm-btn lm-btn--outline" href="{{profileUrl}}" target="_blank" rel="noopener">{{t "profile"}}</a>{{/if}}
  </div>
  {{#if (shouldShowFooter stocking displays)}}
  <div class="lm-card__badges">
    {{#if stocking}}<span class="lm-badge">{{t "stocking"}}</span>{{/if}}
    {{#if displays}}<span class="lm-badge">{{t "onDisplay"}}</span>{{/if}}
  </div>
  {{/if}}
</div>`,
  infowindow: `<div class="lm-popup__body">
  <strong class="lm-popup__name">{{name}}</strong>
  <div class="lm-popup__line">{{#if street}}{{street}}, {{/if}}{{city}}, {{region}}</div>
  {{#if phone}}<div class="lm-popup__line"><a href="{{tel phoneE164 phone}}">{{phone}}</a></div>{{/if}}
  <div class="lm-popup__line"><a href="{{directionsUrl this}}" target="_blank" rel="noopener">{{t "directions"}}</a></div>
  {{#if hasDistance}}<div class="lm-popup__line lm-popup__meta">{{distanceText}}</div>{{/if}}
  {{#if profileUrl}}<div class="lm-popup__line"><a class="lm-btn lm-btn--outline lm-btn--sm" href="{{profileUrl}}" target="_blank" rel="noopener">{{t "profile"}}</a></div>{{/if}}
</div>`,
  noresults: `<div class="lm-empty">
  <strong>{{t "noResultsTitle"}}</strong>
  <p>{{t "noResultsBody"}}</p>
</div>`,
};

/** A realistic record for the admin template preview. */
export const SAMPLE_LOCATION: NormalizedLocation = {
  id: '0012T00001l1abxQAA', sfid: '0012T00001l1abxQAA', dataset: 'retailers', type: 'retailer', typeId: 1,
  name: 'Sample Building Supply', street: '100 Ottawa St S', street2: null, city: 'Kitchener', region: 'Ontario', regionCode: 'ON',
  country: 'CA', countryName: 'Canada', zip: 'N2G 3S8', lat: 43.4386, lng: -80.4826,
  phone: '519-555-0100', phoneE164: '+15195550100', url: 'https://example.com', email: null, placeId: '',
  stocking: true, displays: true, contactFirst: 'Jordan', contactLast: 'H.', buyingGroup: 'Home Hardware', performanceScore: 0, radius: null,
  logo: '', logoSquare: '', pin: '', hasProfile: true, profileUrl: 'https://trusscore.com/locations/retailer/0012T00001l1abxQAA', distance: 12.4,
  flags: { residential: false, commercial: false, agriculture: false, wcb: true, sw: true, dd: false, nl: false, rc: false, tw: false, trim: true },
  showAddress: true, cardTpl: null, infowindowTpl: null,
};

/** Placeholder legend shown next to the template editors. */
export const PLACEHOLDER_LEGEND: { field: string; description: string }[] = [
  { field: 'id / sfid', description: 'Location id (Salesforce account id for retailers, public id for installers) / Salesforce id' },
  { field: 'dataset / type', description: '"retailers" or "installers" / retailer, generic, showroom, installer' },
  { field: 'name', description: 'Business name' },
  { field: 'street / street2 / city / region / regionCode / zip', description: 'Address parts. street, street2 and zip are blank for installers who hide their address' },
  { field: 'country / countryName', description: 'ISO-2 code / English name' },
  { field: 'lat / lng', description: 'Coordinates' },
  { field: 'phone / phoneE164', description: 'Formatted phone / dial-able +1… form (use the tel helper)' },
  { field: 'url / email', description: "The business's website / email (when trusscore.com supplies one — both datasets)" },
  { field: 'selectable / isSelected / isDisabled / selectLabel', description: 'Share picker only: whether this map lets the visitor pick locations, the current state of this one (picked / already sent), and the label for a data-lm-action="select" control' },
  { field: 'stocking / displays', description: 'Retailers: carries stock / has a display (null for installers)' },
  { field: 'contactFirst / contactLast', description: 'Trusscore contact first name / last initial' },
  { field: 'buyingGroup', description: 'Retailer buying group (e.g. Home Hardware)' },
  { field: 'radius', description: 'Installer service radius in km' },
  { field: 'logo / logoSquare / pin', description: 'Image URLs (may be blank)' },
  { field: 'hasProfile / profileUrl', description: 'Whether a trusscore.com profile page exists / its URL' },
  { field: 'distance / hasDistance / distanceText', description: 'Distance from the search point in the map units / whether one exists / formatted "12.4 km away"' },
  { field: 'flags.residential … flags.trim', description: 'Booleans: residential, commercial, agriculture (installers); wcb, sw, dd, nl, rc, tw, trim (retailer product flags)' },
  { field: 'showAddress', description: 'False when the street/postal code are withheld' },
];

export const HELPER_LEGEND: { helper: string; description: string }[] = [
  { helper: '{{t "key"}}', description: 'Translatable copy — the map\'s own Strings first, else the built-in text. Keys: ' + MAP_STRING_KEYS.join(', ') },
  { helper: '{{tel phoneE164 phone}}', description: 'A tel: link target (prefers the +1 form)' },
  { helper: '{{directionsUrl this}}', description: 'Google Maps directions link to this location' },
  { helper: '{{#if (eq type "showroom")}}…{{/if}}', description: 'Equality test' },
  { helper: '{{#if (shouldShowFooter stocking displays)}}…{{/if}}', description: 'True when either badge applies' },
  { helper: '{{default buyingGroup "—"}}', description: 'Fallback when a value is blank' },
  { helper: '{{join someList ", "}}', description: 'Join a list' },
  { helper: 'data-lm-action="show"', description: 'Put this attribute on any element to make it pan to the pin and open its popup' },
  { helper: 'data-lm-action="select"', description: 'Share picker maps only: picks this location. If a card / popup template has no element with this attribute, a default "Send estimate here" button is added to .lm-card__actions (or the card) automatically — so existing templates keep working' },
];

/** Lock presets — same boxes as the website's bounds.json; masks live in public/maps/boundaries/. */
export const LOCK_PRESETS: Record<Exclude<MapLockPreset, 'none'>, { label: string; members: string[]; bbox: [number, number, number, number]; fit: [number, number, number, number]; minZoom: number; mask: string }> = {
  'ca':            { label: 'Canada',          members: ['CA'],             bbox: [-141, 41.7, -52.6, 83.1],   fit: [-141, 41.7, -52.6, 72],   minZoom: 2, mask: 'ca.geojson' },
  'us':            { label: 'United States',   members: ['US'],             bbox: [-179.2, 18.9, -66.9, 71.4], fit: [-124.85, 24.4, -66.9, 49.4], minZoom: 2, mask: 'us.geojson' },
  'us-lower48':    { label: 'United States (lower 48)', members: ['US'],    bbox: [-124.85, 24.4, -66.9, 49.4], fit: [-124.85, 24.4, -66.9, 49.4], minZoom: 3, mask: 'us-lower48.geojson' },
  'mx':            { label: 'Mexico',          members: ['MX'],             bbox: [-118.4, 14.5, -86.7, 32.7], fit: [-118.4, 14.5, -86.7, 32.7], minZoom: 3, mask: 'mx.geojson' },
  'north-america': { label: 'North America',   members: ['CA', 'US', 'MX'], bbox: [-170, 14.5, -52, 83.1],     fit: [-141, 24, -52.6, 62],       minZoom: 2, mask: 'north-america.geojson' },
  'ky':            { label: 'Cayman Islands',  members: ['KY'],             bbox: [-81.6, 19.2, -79.6, 19.9],  fit: [-81.6, 19.2, -79.6, 19.9],  minZoom: 8, mask: 'ky.geojson' },
};
